import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useToast } from '../components/ToastContainer';
import './SendMessagePage.css';

interface ActiveSession {
  sessionId: string;
  sessionIdDisplay?: string;
  userId?: number;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  isGuest: boolean;
}

interface Message {
  id: string;
  sessionId?: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'announcement';
  title?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  expiresAt?: string;
  sentBy?: {
    userId: number;
    userEmail: string;
    userName?: string;
  };
}

const SendMessagePage: React.FC = () => {
  const { showToast } = useToast();
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Form state
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [messageType, setMessageType] = useState<'info' | 'warning' | 'error' | 'success' | 'announcement'>('info');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [expiresInMinutes, setExpiresInMinutes] = useState<number | ''>('');

  useEffect(() => {
    fetchSessions();
    fetchMessages();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await authAPI.getActiveSessions();
      setSessions(response.data.sessions || []);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
      showToast('Failed to fetch active sessions', 'error');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await authAPI.getAllMessages();
      setMessages(response.data.messages || []);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      showToast('Failed to fetch messages', 'error');
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      showToast('Please enter a message', 'error');
      return;
    }

    setSending(true);
    try {
      await authAPI.sendMessage({
        sessionId: selectedSessionId || undefined,
        message: message.trim(),
        title: title.trim() || undefined,
        type: messageType,
        priority,
        expiresInMinutes: expiresInMinutes ? Number(expiresInMinutes) : undefined
      });

      showToast(
        selectedSessionId 
          ? 'Message sent to session successfully' 
          : 'Broadcast message sent successfully',
        'success'
      );

      // Reset form
      setSelectedSessionId('');
      setMessage('');
      setTitle('');
      setMessageType('info');
      setPriority('medium');
      setExpiresInMinutes('');

      // Refresh messages
      await fetchMessages();
    } catch (error: any) {
      console.error('Error sending message:', error);
      showToast(error.response?.data?.message || 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await authAPI.deleteMessage(messageId);
      showToast('Message deleted successfully', 'success');
      await fetchMessages();
    } catch (error: any) {
      console.error('Error deleting message:', error);
      showToast('Failed to delete message', 'error');
    }
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'urgent': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'success': return '#28a745';
      case 'announcement': return '#007bff';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  return (
    <div className="send-message-page">
      <div className="send-message-header">
        <h1>📨 Send Messages</h1>
        <p>Send messages to specific sessions or broadcast to all active sessions</p>
      </div>

      <div className="send-message-content">
        <div className="send-message-form-section">
          <h2>Compose Message</h2>
          <form onSubmit={handleSendMessage} className="message-form">
            <div className="form-group">
              <label htmlFor="sessionId">Target Session (leave empty for broadcast)</label>
              <select
                id="sessionId"
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
                className="form-control"
              >
                <option value="">📢 Broadcast to All Sessions</option>
                {sessions.map((session) => (
                  <option key={session.sessionId} value={session.sessionId}>
                    {session.userName || session.userEmail || 'Guest'} - {session.sessionIdDisplay || session.sessionId.substring(0, 8) + '...'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="title">Title (optional)</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="form-control"
                placeholder="Message title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="form-control"
                rows={4}
                placeholder="Enter your message here..."
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="messageType">Type</label>
                <select
                  id="messageType"
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value as any)}
                  className="form-control"
                >
                  <option value="info">ℹ️ Info</option>
                  <option value="success">✅ Success</option>
                  <option value="warning">⚠️ Warning</option>
                  <option value="error">❌ Error</option>
                  <option value="announcement">📢 Announcement</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as any)}
                  className="form-control"
                >
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🟠 High</option>
                  <option value="urgent">🔴 Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="expiresInMinutes">Expires In (minutes, optional)</label>
                <input
                  type="number"
                  id="expiresInMinutes"
                  value={expiresInMinutes}
                  onChange={(e) => setExpiresInMinutes(e.target.value ? Number(e.target.value) : '')}
                  className="form-control"
                  placeholder="e.g., 60"
                  min="1"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="send-message-btn"
              disabled={sending || !message.trim()}
            >
              {sending ? '⏳ Sending...' : selectedSessionId ? '📤 Send to Session' : '📢 Broadcast Message'}
            </button>
          </form>
        </div>

        <div className="messages-list-section">
          <div className="messages-header">
            <h2>Recent Messages</h2>
            <button onClick={fetchMessages} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <p>Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="no-messages">
              <p>No messages sent yet</p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((msg) => (
                <div key={msg.id} className="message-item">
                  <div className="message-header">
                    <div className="message-meta">
                      <span 
                        className="message-type-badge"
                        style={{ backgroundColor: getTypeColor(msg.type) }}
                      >
                        {msg.type}
                      </span>
                      <span 
                        className="message-priority-badge"
                        style={{ backgroundColor: getPriorityColor(msg.priority) }}
                      >
                        {msg.priority}
                      </span>
                      {msg.sessionId ? (
                        <span className="message-target">To: {msg.sessionId.substring(0, 8)}...</span>
                      ) : (
                        <span className="message-target">📢 Broadcast</span>
                      )}
                    </div>
                    <button
                      className="delete-message-btn"
                      onClick={() => handleDeleteMessage(msg.id)}
                      title="Delete message"
                    >
                      🗑️
                    </button>
                  </div>
                  
                  {msg.title && (
                    <div className="message-title">{msg.title}</div>
                  )}
                  
                  <div className="message-content">{msg.message}</div>
                  
                  <div className="message-footer">
                    <span className="message-time">
                      Sent: {formatTime(msg.createdAt)}
                    </span>
                    {msg.expiresAt && (
                      <span className="message-expires">
                        Expires: {formatTime(msg.expiresAt)}
                      </span>
                    )}
                    {msg.sentBy && (
                      <span className="message-sender">
                        By: {msg.sentBy.userName || msg.sentBy.userEmail}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendMessagePage;

